import { useState } from 'react'
import { ArrowLeft, KeyRound, Loader2, ShieldCheck, Wifi } from 'lucide-react'
import type { BrokerLoginDto, BrokerProviderDto } from '@apna/shared'
import { api, ApiRequestError } from '@/lib/api'
import { cn } from '@/lib/utils'

/**
 * Connect-a-broker page, matching trading-journal's Zerodha setup screen:
 * back link, logo + title, "Credentials are encrypted" badge, a two-column
 * body with the form left and setup instructions right.
 *
 * ApnaAlgo's difference is structural, not cosmetic. The journal collects one
 * credential pair per account. XTS needs TWO ([B1] — Interactive for orders,
 * Market Data for prices), and a DEALER login serves many client codes. So
 * this screen creates a LOGIN and a CONNECTION in one pass, and the
 * credential fields it renders come from `provider.credentialRoles` rather
 * than being hardcoded — which is what lets a one-pair broker reuse the same
 * screen unchanged.
 */

interface Props {
  provider: BrokerProviderDto
  onBack: () => void
  onDone: () => void
}

const inputCls =
  'mt-1.5 h-10 w-full rounded-lg border border-input bg-background px-3 text-sm text-foreground outline-none transition-colors focus:border-ring'

const labelCls = 'text-[13px] font-medium text-foreground'

export function ConnectBroker({ provider, onBack, onDone }: Props) {
  const needsMarketData = provider.credentialRoles.includes('MARKET_DATA')

  const [nickname, setNickname] = useState('')
  const [clientCode, setClientCode] = useState('')
  /**
   * The broker's own API host. Mandatory — see the field's own note.
   */
  const [baseUrl, setBaseUrl] = useState('')

  const [interactiveKey, setInteractiveKey] = useState('')
  const [interactiveSecret, setInteractiveSecret] = useState('')

  const [mdKey, setMdKey] = useState('')
  const [mdSecret, setMdSecret] = useState('')

  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    setBusy(true)
    setError(null)

    try {
      /*
       * Credentials first, then the account that uses them.
       *
       * Not transactional across the two calls, and that is the safer order:
       * a login with no connection is harmless and reusable, whereas a
       * connection with no credential cannot trade and looks broken. If the
       * second call fails the user retries the account, not the secret.
       */
      let interactiveLoginId: string | undefined
      if (interactiveKey.trim() && interactiveSecret.trim()) {
        const login = await api.post<BrokerLoginDto>('/broker/logins', {
          providerKey: provider.key,
          role: 'INTERACTIVE',
          label: `${nickname.trim() || clientCode.trim()} — orders`,
          apiKey: interactiveKey.trim(),
          apiSecret: interactiveSecret.trim(),
        })
        interactiveLoginId = login.id
      }

      let marketDataLoginId: string | undefined
      // Filled in means wanted. There is no platform feed to opt out to.
      if (mdKey.trim() && mdSecret.trim()) {
        const login = await api.post<BrokerLoginDto>('/broker/logins', {
          providerKey: provider.key,
          role: 'MARKET_DATA',
          label: `${nickname.trim() || clientCode.trim()} — feed`,
          apiKey: mdKey.trim(),
          apiSecret: mdSecret.trim(),
        })
        marketDataLoginId = login.id
      }

      await api.post('/broker/connections', {
        providerKey: provider.key,
        clientCode: clientCode.trim(),
        label:
          nickname.trim() || `${provider.displayName} · ${clientCode.trim()}`,
        baseUrl: baseUrl.trim(),
        // LIVE is the only mode offered. The column and the paper broker
        // both remain; what is gone is the question at setup time.
        mode: 'LIVE',
        interactiveLoginId,
        marketDataLoginId,
      })

      onDone()
    } catch (err) {
      const detail = err instanceof ApiRequestError ? err.detail : undefined
      const issues = (detail?.issues as { message: string }[] | undefined)?.map(
        (i) => i.message,
      )
      setError(issues?.join(' ') ?? (err as Error).message)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="w-full space-y-4 pb-6">
      <button
        onClick={onBack}
        className="inline-flex items-center gap-1.5 text-xs font-medium text-muted-foreground transition-colors hover:text-foreground"
      >
        <ArrowLeft className="h-3.5 w-3.5" /> Back to Broker Connections
      </button>

      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <div
            className="inline-flex shrink-0 items-center justify-center rounded-lg border bg-background"
            style={{ width: 40, height: 40 }}
          >
            <KeyRound className="h-5 w-5 text-muted-foreground" />
          </div>
          <div>
            <h1 className="text-xl font-semibold leading-none tracking-tight text-foreground">
              Connect {provider.displayName}
            </h1>
            <p className="mt-1.5 text-xs text-muted-foreground">
              Set up your {provider.displayName} account to place and manage
              orders.
            </p>
          </div>
        </div>

        <span className="inline-flex shrink-0 items-center gap-1.5 rounded-lg border px-3 py-1.5 text-[11px] font-medium text-muted-foreground">
          <ShieldCheck className="h-3.5 w-3.5" /> Credentials are encrypted
        </span>
      </div>

      <form
        onSubmit={submit}
        className="grid grid-cols-1 gap-4 lg:grid-cols-[1fr_320px]"
      >
        {/* ── left: the form ──────────────────────────────────────────────── */}
        <div className="space-y-4 rounded-xl border bg-card p-5 shadow-sm">
          <div>
            <h2 className="text-sm font-semibold text-foreground">
              {provider.displayName} Setup Details
            </h2>
            <p className="mt-0.5 text-xs text-muted-foreground">
              Name the account and give it a client code — credentials are
              optional and can be added later.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <label className="block">
              <span className={labelCls}>
                Account nickname <span className="text-destructive">*</span>
              </span>
              <input
                value={nickname}
                onChange={(e) => setNickname(e.target.value)}
                required
                placeholder="e.g. Desk Primary"
                className={inputCls}
              />
            </label>

            <label className="block">
              <span className={labelCls}>Client code</span>
              <input
                value={clientCode}
                onChange={(e) => setClientCode(e.target.value.toUpperCase())}
                required
                placeholder="e.g. AB12"
                spellCheck={false}
                className={cn(inputCls, 'num')}
              />
              <span className="mt-1 block text-[11px] text-muted-foreground">
                The broker&apos;s own code for this account. Must be unique.
              </span>
            </label>
          </div>

          <label className="block">
            <span className={labelCls}>
              Broker API URL <span className="text-destructive">*</span>
            </span>
            <input
              value={baseUrl}
              onChange={(e) => setBaseUrl(e.target.value)}
              required
              placeholder="http://tradewiin.wiinance.com:3000"
              spellCheck={false}
              className={cn(inputCls, 'num')}
              data-testid="base-url"
            />
            <span className="mt-1 block text-[11px] text-muted-foreground">
              {/*
                Asked rather than assumed: XTS is a software product, not a
                company. Wiinance, Symphony and every other XTS broker runs
                its own deployment on its own host, so there is no default
                that is right for everyone. Your broker gives you this with
                the API keys. One host serves both APIs.
              */}
              Your broker gives you this with the API keys — the same host
              serves orders and market data.
            </span>
          </label>

          {/* Interactive credentials */}
          <div className="rounded-lg border p-4">
            <div className="flex items-center gap-2">
              <KeyRound className="h-3.5 w-3.5 text-muted-foreground" />
              <h3 className="text-[13px] font-semibold text-foreground">
                Interactive API (orders)
              </h3>
            </div>
            <p className="mt-1 text-xs text-muted-foreground">
              Places and manages orders. This is the pair that can move money.
            </p>

            <div className="mt-3 grid grid-cols-2 gap-4">
              <label className="block">
                <span className={labelCls}>API Key</span>
                <input
                  value={interactiveKey}
                  onChange={(e) => setInteractiveKey(e.target.value)}
                  placeholder="Paste your Interactive key"
                  autoComplete="off"
                  spellCheck={false}
                  className={cn(inputCls, 'num')}
                />
              </label>
              <label className="block">
                <span className={labelCls}>Secret Key</span>
                <input
                  type="password"
                  value={interactiveSecret}
                  onChange={(e) => setInteractiveSecret(e.target.value)}
                  placeholder="Paste your Interactive secret"
                  autoComplete="new-password"
                  className={cn(inputCls, 'num')}
                />
              </label>
            </div>
          </div>

          {/* Market data — only for brokers that use a second pair */}
          {needsMarketData && (
            <div className="rounded-lg border p-4">
              <div className="flex items-center gap-2">
                <Wifi className="h-3.5 w-3.5 text-muted-foreground" />
                <h3 className="text-[13px] font-semibold text-foreground">
                  Market Data API
                </h3>
              </div>
              <p className="mt-1 text-xs text-muted-foreground">
                Streams live prices. Without this pair the account can trade but
                shows no prices — your broker allows one streaming session per
                market-data key, so this pair is yours alone.
              </p>

              <div className="mt-3 grid grid-cols-2 gap-4">
                <label className="block">
                  <span className={labelCls}>API Key</span>
                  <input
                    value={mdKey}
                    onChange={(e) => setMdKey(e.target.value)}
                    autoComplete="off"
                    spellCheck={false}
                    placeholder="Paste your Market Data key"
                    className={cn(inputCls, 'num')}
                  />
                </label>
                <label className="block">
                  <span className={labelCls}>Secret Key</span>
                  <input
                    type="password"
                    value={mdSecret}
                    onChange={(e) => setMdSecret(e.target.value)}
                    autoComplete="new-password"
                    placeholder="Paste your Market Data secret"
                    className={cn(inputCls, 'num')}
                  />
                </label>
              </div>
            </div>
          )}

          <p className="rounded-lg border border-warning/30 bg-warning/5 px-3 py-2 text-xs leading-relaxed text-warning">
            This account places real orders.
          </p>

          {error && (
            <p className="rounded-lg border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">
              {error}
            </p>
          )}

          <div className="flex items-center justify-end gap-2 border-t pt-4">
            <button
              type="button"
              onClick={onBack}
              className="inline-flex h-9 items-center rounded-lg border px-4 text-sm font-medium transition-colors hover:bg-accent"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={busy}
              className="inline-flex h-9 items-center gap-2 rounded-lg bg-primary px-5 text-sm font-medium text-primary-foreground shadow-sm transition hover:opacity-90 disabled:opacity-50"
              data-testid="connect-submit"
            >
              {busy && <Loader2 className="h-4 w-4 animate-spin" />}
              Add
            </button>
          </div>
        </div>

        {/* ── right: instructions ─────────────────────────────────────────── */}
        <aside className="space-y-4">
          <div className="rounded-xl border bg-card p-4 shadow-sm">
            <h3 className="text-[13px] font-semibold text-foreground">
              Setup instructions
            </h3>
            <ol className="mt-2.5 space-y-2.5 text-[11px] leading-relaxed text-muted-foreground">
              <li>
                <span className="font-medium text-foreground">1.</span> Ask your
                broker for XTS API access. They issue the keys — there is no
                self-service portal.
              </li>
              <li>
                <span className="font-medium text-foreground">2.</span> You will
                receive <span className="font-medium text-foreground">two</span>{' '}
                key/secret pairs and a{' '}
                <span className="font-medium text-foreground">server URL</span>: one pair for
                the <span className="font-medium text-foreground">Interactive</span> API
                (orders) and one for{' '}
                <span className="font-medium text-foreground">Market Data</span> (prices). They
                are not interchangeable, and both are needed — orders alone will not show you a
                price.
              </li>
              <li>
                <span className="font-medium text-foreground">3.</span> Give the
                broker this server&apos;s static IP to whitelist. Order
                placement is IP-restricted even when reads are not.
              </li>
              <li>
                <span className="font-medium text-foreground">4.</span> Paste
                the URL and both pairs here and save.
              </li>
              <li>
                <span className="font-medium text-foreground">5.</span> Run{' '}
                <span className="font-medium text-foreground">
                  Test order capability
                </span>{' '}
                from the accounts table. A green tick means orders will actually
                place — not merely that the account connects.
              </li>
            </ol>
          </div>

          <div className="rounded-xl border bg-card p-4 shadow-sm">
            <h3 className="text-[13px] font-semibold text-foreground">
              About dealer logins
            </h3>
            <p className="mt-2 text-[11px] leading-relaxed text-muted-foreground">
              If your broker issued a{' '}
              <span className="font-medium text-foreground">dealer</span> login,
              one credential pair acts for many client codes — add the
              credential once, then add an account per code and point each at
              the same credential.
            </p>
            <p className="mt-2 text-[11px] leading-relaxed text-muted-foreground">
              Dealer logins have no funds API, so the Capital column will read{' '}
              <span className="font-medium text-foreground">Unavailable</span>.
              That is XTS&apos;s restriction, not a connection problem.
            </p>
          </div>
        </aside>
      </form>
    </div>
  )
}
